import { Redirect, useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  IconDots,
  IconFileText,
  IconInfoCircle,
  IconLink,
  IconStar,
} from "@tabler/icons-react";
import { Button } from "../../../components/ui/Button";
import { usePopoverMenu } from "../../../components/ui/Popover";
import {
  DesktopMenu,
  DesktopMenuItem,
} from "../../../components/ui/DesktopMenu";
import ShareV2Icon from "../../../assets/icons/share-v2.svg?react";
import { shareUrl } from "../../../domains/app/utils/shareUrl";
import { displayToast } from "../../../components/app/AppToast";
import { RoomSupportedByChip } from "../../../components/room/RoomSupportedByChip";
import { openDesktopRoomInfo } from "../../../components/room/DesktopRoomInfoHost";
import { useIsRoomMember } from "../../../domains/room/hooks/useIsRoomMember";
import { contactLineKind } from "../../../domains/partners/roomWelcome";
import { gaEventRoomInfoOpened } from "../../../tools/analytics/domains/partners.analytics";
import { useApiQuery, type Api } from "../../../services/api";
import { useActiveLang } from "../../../domains/app/hooks/useActiveLang";
import { useEffectOnce } from "../../../domains/app/hooks/useEffectOnce";
import { useLocationState } from "../../../domains/app/hooks/useLocationState";
import { useNow } from "../../../common/hooks/useNow";
import type { SupportedLanguage } from "../../../interfaces";
import { canGoBackInApp } from "../../../domains/app/utils/backNavigation";
import { getRoomName } from "../../../domains/room/hooks/useRoomName";
import { getRoomLogoUrl } from "../../../domains/room/utils/getRoomLogoUrl";
import { resolveCongressNames } from "../../../domains/congress/congressName";
import { resolveCongressCountdownCopy } from "../../../domains/congress/congressCountdownCopy";
import { deriveCongressPhase } from "../../../../../api/src/shared/domain/congress-lifecycle";
import { deriveCongressStatus } from "../../../domains/congress/congressStatus";
import { buildCongressDateLabel } from "../../../domains/congress/congressDateLabel";
import { DEFAULT_COVER_COLOR } from "../../../domains/congress/coverColor";
import { partnerAccentColor } from "../../../domains/congress/partnerAccentColor";
import { resolveTopSessionsHeading } from "../../../domains/congress/topSessionsHeading";
import { downloadCongressCalendarFile } from "../../../domains/congress/congressCalendar";
import { isNativeApp } from "../../../common/hooks/useIsDesktopView";
import {
  gaEventCongressPdfCardOpened,
  gaEventCongressSessionLinkClicked,
  gaEventCongressSpaceViewed,
  type CongressAnalyticsContext,
} from "../../../tools/analytics/domains/congress.analytics";
import { DesktopTopNav } from "../../../components/app/desktopTopNav/DesktopTopNav";
import { DesktopBreadcrumb } from "../../../components/app/desktopTopNav/DesktopBreadcrumb";
import { Avatar } from "../../../components/ui/Avatar";
import Loader from "../../../components/Loader";
import { DesktopRoomLine } from "../../../components/room/DesktopRoomLine";
import { DesktopCongressCover } from "../../../components/congress/DesktopCongressCover";
import { DesktopCongressPhaseBand } from "../../../components/congress/DesktopCongressPhaseBand";
import { DesktopCongressSessionCard } from "../../../components/congress/DesktopCongressSessionCard";
import {
  CongressDocumentCard,
  CongressSpaceContent,
} from "../../../components/room/CongressSpaceContent";
import { RailSectionTitle } from "../../../components/discovery/DesktopExploreRail";
import { DesktopCongressPdfViewer } from "../../../components/congress/DesktopCongressPdfViewer";
import { DesktopCongressQrCode } from "../../../components/congress/DesktopCongressQrCode";
import { roomLineSubtitle } from "../../../domains/room/utils/roomContentCounts";

const MINUTE_MS = 60_000;
const DOCUMENT_PARAM = "document";

type Room = Api["get"]["/webapp/private/rooms/:id"]["output"]["body"];
type Space =
  Api["get"]["/webapp/private/rooms/:id/congress-spaces"]["output"]["body"][number];
type ContentBlock =
  Api["get"]["/webapp/private/congress-spaces/:id/content"]["output"]["body"][number];
type PdfBlock = Extract<ContentBlock, { kind: "pdfCard" }> & {
  pdfUrl: string;
};

const locationStateSchema = z.object({
  congressEntryPoint: z
    .enum(["banner", "room_block", "chip", "resources_tab"])
    .nullable()
    .default(null),
  // How this entry was reached, for the two Back decisions below:
  // - `arrived`: any other way in; Back reads the router history.
  // - `document-opened-here`: a document pushed from this page; closing it
  //   goes back.
  // - `cold`: a link opened the page on a document and the reader closed it.
  //   Nothing of ours is behind it, whatever router key the `replace` left.
  navigation: z
    .enum(["arrived", "document-opened-here", "cold"])
    .default("arrived"),
});

function initialsOf(name: string): string {
  const letters = name
    .trim()
    .split(/\s+/)
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase();
  return letters === "" ? "?" : letters.slice(0, 2);
}

/**
 * A congress space as a page of its own, under its Room (Figma "11 · Congrès —
 * Congress Space desktop"): ‹ Explore / Room / short name. The header, the
 * phase band, then the curator's sessions and everything the partner shared,
 * in a column with a rail of documents beside it. The URL is the one partner
 * e-mails link to, and it stays valid after the congress.
 *
 * `?document=<block id>` opens a PDF over the page, so a document can be linked
 * and Back closes it.
 */
export function DesktopCongressSpacePage({
  roomId,
  spaceId,
  room,
}: {
  roomId: string;
  spaceId: string;
  room: Room;
}): React.JSX.Element {
  const { contentLang } = useActiveLang();
  const spacesQuery = useApiQuery("/webapp/private/rooms/:id/congress-spaces", {
    param: { id: roomId },
    query: { language: contentLang },
  });
  if (spacesQuery.state === "pending") return <Loader expanded />;
  if (spacesQuery.state === "error")
    return <Redirect to={`/room/resources/${roomId}`} />;
  const space = spacesQuery.body.find((candidate) => candidate.id === spaceId);
  // Unpublished, deleted, or no longer placed in this room: the room's
  // Resources is where the reader can see what the room still holds.
  if (space === undefined) return <Redirect to={`/room/resources/${roomId}`} />;
  return (
    <CongressSpaceLoaded
      key={space.id}
      roomId={roomId}
      room={room}
      space={space}
      contentLang={contentLang}
    />
  );
}

function CongressSpaceLoaded({
  roomId,
  room,
  space,
  contentLang,
}: {
  roomId: string;
  room: Room;
  space: Space;
  contentLang: SupportedLanguage;
}): React.JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const now = useNow(MINUTE_MS);
  const menu = usePopoverMenu("right");
  const isMember = useIsRoomMember(roomId);
  const { congressEntryPoint, navigation } =
    useLocationState(locationStateSchema);
  const contentQuery = useApiQuery(
    "/webapp/private/congress-spaces/:id/content",
    { param: { id: space.id }, query: { language: contentLang } }
  );

  const names = resolveCongressNames(
    space.translations,
    space.fallbackName,
    contentLang,
    space.city,
    space.country
  );
  const roomName = getRoomName(room, contentLang);
  const organisationName = room.organisationModel.name;
  const accentColor = partnerAccentColor(room);
  const coverColor =
    space.coverColor === null ? DEFAULT_COVER_COLOR : space.coverColor;
  const status = deriveCongressStatus(space, now);
  const countdownCopy = resolveCongressCountdownCopy({ contentLang, t });
  const dateLabel = buildCongressDateLabel(
    {
      startDate: space.startDate,
      endDate: space.endDate,
      city: names.city,
      country: names.country,
    },
    { contentLang, monthStyle: "short", placeSeparator: " · " }
  );
  const pageUrl = `${window.location.origin}/room/congress/${roomId}/${space.id}`;
  const placeLabel = [names.city, names.country]
    .filter((part) => part !== null && part !== "")
    .join(", ");
  const analytics = {
    spaceId: space.id,
    congressName: names.name,
    phase: deriveCongressPhase(space, now),
    roomId,
  } satisfies CongressAnalyticsContext;

  useEffectOnce(() => {
    gaEventCongressSpaceViewed({
      context: analytics,
      entryPoint:
        congressEntryPoint === null ? "deep_link" : congressEntryPoint,
    });
  }, [space.id]);

  const blocks = contentQuery.state === "success" ? contentQuery.body : [];
  const documents = blocks.flatMap((block): PdfBlock[] =>
    block.kind === "pdfCard" && block.pdfUrl !== null
      ? [{ ...block, pdfUrl: block.pdfUrl }]
      : []
  );
  const documentBlocks = blocks.filter((block) => block.kind === "pdfCard");
  // The partner's floorplan, as the rail draws it (a PDF or an image).
  const floorplanBlock = documentBlocks.find(
    (block) => block.detailsIcon === "location"
  );
  const openDocumentId = new URLSearchParams(location.search).get(
    DOCUMENT_PARAM
  );
  const openDocument =
    openDocumentId === null
      ? undefined
      : documents.find((document) => document.id === openDocumentId);

  // The rank in the space's content order, read by id: the rows hand over a
  // narrowed copy of the block, which `indexOf` would never find.
  const positionOf = (blockId: string): number => {
    const position = blocks.findIndex((candidate) => candidate.id === blockId);
    if (position === -1)
      throw new Error("should not happen: an opened document is a space block");
    return position;
  };

  const openDocumentViewer = (block: PdfBlock) => {
    gaEventCongressPdfCardOpened({
      context: analytics,
      cardId: block.id,
      cardLabel: block.labelType,
      position: positionOf(block.id),
    });
    history.push({
      search: `?${DOCUMENT_PARAM}=${block.id}`,
      state: { navigation: "document-opened-here" },
    });
  };

  // A rail card: a PDF opens the viewer (which reports the open), an image
  // opens its own lightbox and only reports it.
  const openDocumentBlock = (block: (typeof documentBlocks)[number]) => {
    const pdf = documents.find((document) => document.id === block.id);
    if (pdf !== undefined) {
      openDocumentViewer(pdf);
      return;
    }
    gaEventCongressPdfCardOpened({
      context: analytics,
      cardId: block.id,
      cardLabel: block.labelType,
      position: positionOf(block.id),
    });
  };

  const handleBack = () => {
    if (navigation !== "cold" && canGoBackInApp()) {
      history.goBack();
      return;
    }
    history.replace(`/room/discovery/${roomId}`);
  };

  const sessionsPhase =
    status.kind === "live" ? "live" : status.kind === "ended" ? "post" : "pre";
  const topSessions =
    space.topSessionsCount === 0 ? null : (
      <TopSessionsSection
        spaceId={space.id}
        shortName={names.shortName}
        phase={sessionsPhase}
        contentLang={contentLang}
        now={now}
        analytics={analytics}
      />
    );

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <DesktopTopNav
        context={
          <DesktopBreadcrumb
            back={{ kind: "separate", onBack: handleBack }}
            trail={[
              { label: t("Explore"), to: "/discovery" },
              { label: roomName, to: `/room/discovery/${roomId}` },
            ]}
            current={names.shortName}
          />
        }
        trailing={{ kind: "search" }}
        surface="default"
        className={null}
      />
      <div className="min-h-0 flex-1 overflow-y-auto bg-ds-white">
        <div className="px-10 pt-10 pb-24">
          <div className="mx-auto flex max-w-desktop-block flex-col gap-8 font-dm-sans">
            {/* Figma "D3.explore.congress.pre": the room's header grammar,
                the attribution chip and its ⓘ, the dates, Share and the
                menu. */}
            <header className="flex items-start justify-between gap-8">
              <div className="flex min-w-0 max-w-desktop-reading flex-1 flex-col gap-3">
                <h1 className="m-0 text-desktop-title font-semibold text-ds-black">
                  {names.name}
                </h1>
                {names.description === null ||
                names.description === "" ? null : (
                  <p className="m-0 text-desktop-body text-ds-slate-700">
                    {names.description}
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => {
                    // Reported as the room header's ⓘ is: same sheet, same
                    // event (docs/partner-objection.md).
                    gaEventRoomInfoOpened({
                      roomId,
                      isMember,
                      contactLine: contactLineKind(
                        room.partnerContact,
                        isMember
                      ),
                    });
                    openDesktopRoomInfo(roomId);
                  }}
                  className="group flex cursor-pointer items-center gap-1 self-start border-none bg-transparent p-0 text-left"
                >
                  <RoomSupportedByChip
                    organisationName={organisationName}
                    size="md"
                  />
                  <span className="flex size-6 shrink-0 items-center justify-center rounded-md text-ds-slate-400 group-hover:bg-ds-slate-100">
                    <IconInfoCircle className="size-4" stroke={1.25} />
                  </span>
                  <span className="sr-only">{t("About this room")}</span>
                </button>
                {dateLabel === "" ? null : (
                  <p className="m-0 flex items-center gap-1.5 text-footnote text-ds-slate-600">
                    <IconFileText
                      size={16}
                      stroke={1.25}
                      className="shrink-0"
                    />
                    {dateLabel}
                  </p>
                )}
                <div className="flex items-center gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    leftIcon={<ShareV2Icon />}
                    onClick={() => {
                      void shareUrl(
                        names.name,
                        names.name,
                        Promise.resolve(pageUrl)
                      );
                    }}
                  >
                    {t("Share")}
                  </Button>
                  <Button
                    ref={menu.anchor.triggerRef}
                    type="button"
                    variant="outline"
                    size="sm"
                    aria-label={t("More")}
                    aria-haspopup="menu"
                    aria-expanded={menu.isOpen}
                    onClick={menu.toggle}
                    className="w-9 px-0"
                  >
                    <IconDots stroke={1.5} />
                  </Button>
                  {menu.isOpen ? (
                    <DesktopMenu anchor={menu.anchor} className="w-60">
                      <DesktopMenuItem
                        description={null}
                        icon={IconLink}
                        label={t("Copy link")}
                        trailing={null}
                        tone="default"
                        action={{
                          kind: "button",
                          onClick: () => {
                            menu.close();
                            void navigator.clipboard.writeText(pageUrl).then(
                              () => {
                                displayToast(t("Link copied"), "success");
                              },
                              () => {
                                displayToast(
                                  t("Something went wrong. Please try again."),
                                  "error"
                                );
                              }
                            );
                          },
                        }}
                      />
                    </DesktopMenu>
                  ) : null}
                </div>
              </div>
              <DesktopCongressCover
                cover={space.cover}
                coverColor={coverColor}
                name={names.name}
                className="hidden h-45.75 w-100 rounded-2xl lg:block"
              />
            </header>

            <DesktopCongressPhaseBand
              status={status}
              shortName={names.shortName}
              dateLabel={dateLabel}
              coverColor={coverColor}
              contentLang={contentLang}
              countdownCopy={countdownCopy}
              now={now}
              onFindUs={
                // The band opens the floorplan when it is a PDF; an image
                // opens from its card in the rail.
                floorplanBlock === undefined ||
                floorplanBlock.documentKind === "image"
                  ? null
                  : () => {
                      openDocumentBlock(floorplanBlock);
                    }
              }
              // The .ics is a blob: download, which the native shells cannot
              // hand to a calendar app.
              onAddToCalendar={
                !isNativeApp() &&
                (status.kind === "countdown" || status.kind === "upcoming") &&
                space.startDate !== null &&
                space.endDate !== null
                  ? () => {
                      if (space.startDate === null || space.endDate === null)
                        throw new Error("should not happen: dates checked");
                      downloadCongressCalendarFile({
                        uid: space.id,
                        title: names.name,
                        startDate: space.startDate,
                        endDate: space.endDate,
                        location: placeLabel,
                        url: pageUrl,
                      });
                    }
                  : null
              }
            />

            <div className="flex items-start gap-8">
              <div className="flex min-w-0 max-w-desktop-column flex-1 flex-col gap-8">
                <p className="m-0 rounded-lg bg-ds-slate-100 px-3.5 py-2.5 text-footnote text-ds-slate-600">
                  {t("Shared by {{organisation}}", {
                    organisation: organisationName,
                  })}
                </p>
                {/* Before and during the congress the selection leads; after
                    it, what was shared does, and the selection is the record. */}
                {sessionsPhase === "post" ? null : topSessions}
                {/* Figma "04 - Rooms / Congress": the partner's blocks as the
                    room's congress cards, on two columns. */}
                {contentQuery.state === "pending" ? (
                  <Loader />
                ) : contentQuery.state === "error" ? (
                  <p className="m-0 text-footnote text-ds-slate-600">
                    {contentQuery.message}
                  </p>
                ) : !contentQuery.body.some(
                    (block) => block.kind !== "topSessions"
                  ) ? null : (
                  <section className="flex flex-col gap-6">
                    <h2 className="m-0 border-b border-ds-slate-100 pb-2 text-heading-xs font-semibold text-ds-black">
                      {t("More from {{organisationName}} at {{congressName}}", {
                        organisationName,
                        congressName: names.shortName,
                      })}
                    </h2>
                    <div className="grid grid-cols-2 items-start gap-6">
                      <CongressSpaceContent
                        spaceId={space.id}
                        analytics={analytics}
                        language={contentLang}
                        roomId={roomId}
                        accentColor={coverColor}
                        shortName={names.shortName}
                        curatorName={space.curator.name}
                        curatorRole={space.curator.role}
                        curatorAvatarUrl={space.curator.avatarUrl}
                        topSessionsCount={space.topSessionsCount}
                        topSessionsCardTitle={space.topSessionsCardTitle}
                        surface={{
                          kind: "desktop-page",
                          onOpenDocument: (block) => {
                            const document = documents.find(
                              (candidate) => candidate.id === block.id
                            );
                            if (document === undefined)
                              throw new Error(
                                "should not happen: an opened PDF is a space document"
                              );
                            openDocumentViewer(document);
                          },
                        }}
                      />
                    </div>
                  </section>
                )}
                {sessionsPhase === "post" ? topSessions : null}
              </div>

              <aside className="sticky top-8 hidden w-desktop-rail shrink-0 flex-col gap-8 lg:flex">
                {/* Figma: before and during the congress the rail leads
                    with where to find the partner (the document with a
                    place); after it, with every document of the space. */}
                {sessionsPhase === "post" ? (
                  documentBlocks.length === 0 ? null : (
                    <section className="flex flex-col gap-3">
                      <RailSectionTitle
                        title={t("Documents · {{count}}", {
                          count: documentBlocks.length,
                        })}
                        onSeeAll={null}
                      />
                      {documentBlocks.map((block) => (
                        <CongressDocumentCard
                          key={block.id}
                          block={block}
                          accentColor={coverColor}
                          place="desktop-rail"
                          onOpen={() => {
                            openDocumentBlock(block);
                          }}
                        />
                      ))}
                    </section>
                  )
                ) : floorplanBlock === undefined ? null : (
                  <section className="flex flex-col gap-3">
                    <RailSectionTitle
                      title={t("Where to find us")}
                      onSeeAll={null}
                    />
                    <CongressDocumentCard
                      block={floorplanBlock}
                      accentColor={coverColor}
                      place="desktop-rail"
                      onOpen={() => {
                        openDocumentBlock(floorplanBlock);
                      }}
                    />
                  </section>
                )}
                {status.kind === "countdown" ||
                status.kind === "upcoming" ||
                status.kind === "live" ? (
                  <section className="flex flex-col gap-3">
                    <RailSectionTitle
                      title={t("On your phone")}
                      onSeeAll={null}
                    />
                    <div className="flex items-start gap-3">
                      <DesktopCongressQrCode
                        url={pageUrl}
                        label={t("QR code of this page")}
                      />
                      <p className="m-0 text-footnote text-ds-slate-600">
                        {t("Scan to open this page.")}
                      </p>
                    </div>
                  </section>
                ) : null}
                <section className="flex flex-col gap-3">
                  <RailSectionTitle
                    title={t("From the room")}
                    onSeeAll={null}
                  />
                  <DesktopRoomLine
                    nameBadge={null}
                    name={roomName}
                    logoUrl={getRoomLogoUrl(room)}
                    // Two counts, each with its own plural.
                    subtitle={[
                      roomLineSubtitle(room, contentLang, t),
                      t("{{count}} members", { count: room.memberCount }),
                    ].join(" · ")}
                    action={null}
                    onClick={() => {
                      history.push(`/room/discovery/${roomId}`);
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      history.push(`/room/discovery/${roomId}`);
                    }}
                    className="cursor-pointer self-start border-none bg-transparent p-0 text-footnote font-semibold text-ds-mint-800"
                  >
                    {t("Back to the room")}
                  </button>
                </section>
              </aside>
            </div>
          </div>
        </div>
      </div>
      {openDocument === undefined ? null : (
        <DesktopCongressPdfViewer
          label={openDocument.labelType}
          title={openDocument.documentTitle}
          organisationName={organisationName}
          accentColor={accentColor}
          pdfUrl={openDocument.pdfUrl}
          onClose={() => {
            // Opened from the page: going back closes it without leaving an
            // entry that Back would reopen. Opened by a link: nothing of ours
            // is behind it, so the param is dropped in place and the entry
            // stays marked cold for the breadcrumb's Back.
            if (navigation === "document-opened-here") {
              history.goBack();
              return;
            }
            history.replace({ search: "", state: { navigation: "cold" } });
          }}
        />
      )}
    </div>
  );
}

/**
 * The curator's Top sessions. The section exists only when the room payload
 * counts published sessions in the reader's language, so the query has rows.
 */
function TopSessionsSection({
  spaceId,
  shortName,
  phase,
  contentLang,
  now,
  analytics,
}: {
  spaceId: string;
  shortName: string;
  phase: "pre" | "live" | "post";
  contentLang: SupportedLanguage;
  now: Date;
  analytics: CongressAnalyticsContext;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const query = useApiQuery(
    "/webapp/private/congress-spaces/:id/top-sessions",
    { param: { id: spaceId }, query: { language: contentLang } }
  );
  if (query.state === "pending") return null;
  if (query.state === "error")
    return (
      <p className="m-0 text-footnote text-ds-slate-600">{query.message}</p>
    );
  const { sessions, curator } = query.body;
  const ranksAt = new Map<string, number[]>();
  for (const [index, session] of sessions.entries()) {
    const ranks = ranksAt.get(session.startsAt);
    ranksAt.set(session.startsAt, [
      ...(ranks === undefined ? [] : ranks),
      index + 1,
    ]);
  }

  const sessionsGrid = (
    <div className="grid grid-cols-2 items-start gap-6">
      {sessions.map((session, index) => {
        const sameStart = ranksAt.get(session.startsAt);
        if (sameStart === undefined)
          throw new Error("should not happen: every session start is indexed");
        return (
          <DesktopCongressSessionCard
            key={session.id}
            rank={index + 1}
            session={session}
            phase={phase}
            sameTimeAs={sameStart.filter((rank) => rank !== index + 1)}
            contentLang={contentLang}
            now={now}
            onLinkClick={() => {
              gaEventCongressSessionLinkClicked({
                context: analytics,
                sessionId: session.id,
                position: index,
              });
            }}
          />
        );
      })}
    </div>
  );
  const heading = resolveTopSessionsHeading({
    cardTitle: query.body.topSessionsCardTitle,
    shortName,
    sessionCount: sessions.length,
    contentLang,
    t,
  });
  // Figma "D3.explore.congress.pre": the section header, then before the
  // congress the curator's card (DS "Congress-top-sessions", Session Page)
  // beside their note, then the sessions as cards on two columns.
  return (
    <section className="flex flex-col gap-6">
      <div className="flex flex-wrap items-baseline gap-2 border-b border-ds-slate-100 pb-2">
        <h2 className="m-0 text-heading-xs font-semibold text-ds-black">
          {heading}
        </h2>
        {curator.name === null ? null : (
          <span className="text-footnote text-ds-slate-600">
            {/* The admin-authored role, printed as written: it replaced the
                translated "Selected by" (docs/congress-space.md). */}
            {`${curator.role} · ${curator.name}`}
          </span>
        )}
      </div>
      {phase === "pre" && curator.name !== null ? (
        <div className="flex items-start gap-8">
          <div className="flex w-82 shrink-0 flex-col items-start gap-1.5 rounded-2xl bg-ds-mint-50 p-4">
            <p className="m-0 flex items-center gap-1.5 text-ui-body font-bold text-ds-mint-800 uppercase">
              <IconStar className="size-4 shrink-0" stroke={1.25} />
              {heading}
            </p>
            <div className="flex items-center gap-1.5">
              {curator.avatarUrl === null ? (
                <Avatar
                  variant="initials"
                  initials={initialsOf(curator.name)}
                  size="lg"
                  badge={null}
                />
              ) : (
                <Avatar
                  variant="image"
                  src={curator.avatarUrl}
                  alt={curator.name}
                  imageFit="cover"
                  size="lg"
                  badge={null}
                />
              )}
              <div className="flex min-w-0 flex-col">
                <span className="text-caption text-ds-mint-700 uppercase">
                  {curator.role}
                </span>
                <span className="text-footnote font-bold text-ds-mint-800">
                  {curator.name}
                </span>
              </div>
            </div>
            {curator.bio === null || curator.bio === "" ? null : (
              <p className="m-0 text-caption text-ds-mint-800">{curator.bio}</p>
            )}
          </div>
          {curator.selectionNote === null ||
          curator.selectionNote === "" ? null : (
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <p className="m-0 text-footnote font-semibold text-ds-black">
                {t("Curator's note")}
              </p>
              <p className="m-0 text-desktop-body text-ds-slate-700">
                “{curator.selectionNote}”
              </p>
            </div>
          )}
        </div>
      ) : null}
      {sessionsGrid}
    </section>
  );
}
